/**
 * auth.ts — session, membership and invite shapes (P2/US-205).
 *
 * ────────────────────────────────────────────────────────────────────────────────────────────────
 * THE ONE ADDITION TO A FROZEN CONTRACT, AND ITS WRITTEN REASON
 * ────────────────────────────────────────────────────────────────────────────────────────────────
 * `README.md`'s freeze notice permits an additive change **with a written reason**. This file makes
 * one: `apiErrorSchema` gains a `401 unauthenticated` member.
 *
 * It is additive by construction. No existing client has ever branched on a `401` from this API,
 * because until P2 there was no authentication and the status could not occur. A client written
 * against v1 that receives one is seeing a status it has no handler for either way — exactly as it
 * would for a `502` from a proxy — so nothing that worked stops working. That is the test the
 * freeze notice is really asking: *would a client that integrated against v1 keep working?* Yes.
 *
 * What would NOT have been additive, and was therefore not done: changing an existing body,
 * renaming a field, or making `401` replace one of the statuses a client already handles. Those
 * need a `/v2` route.
 *
 * ────────────────────────────────────────────────────────────────────────────────────────────────
 * WHAT THIS FILE DOES NOT CONTAIN
 * ────────────────────────────────────────────────────────────────────────────────────────────────
 * No password, no token, no session secret, and no hash of any of them. better-auth owns
 * credentials and their storage; this file describes only what the API hands to a browser and what
 * a browser may hand back. A credential shape published in the package that ships to the client is
 * a credential shape an attacker gets to read.
 */

import { z } from 'zod';

import { userRoleSchema } from './db';

/* ================================================================================================
 * 401 — the new error member
 * ============================================================================================== */

/**
 * `401 Unauthorized` — no session, an expired one, or one that no longer resolves to a user.
 *
 * Deliberately **incurious**. It does not say whether the session existed, whether it expired, or
 * whether the user was deleted, because each of those is a fact about somebody's account that an
 * unauthenticated caller has not earned. "Sign in again" is the only action available for all
 * three, so one message serves the honest user and tells a probing one nothing.
 *
 * Distinct from `403`, which this API does not emit for tenancy: another organisation's resource is
 * **not found**, because a `403` would confirm the id is real (see `DATA.md`).
 */
export const unauthenticatedErrorSchema = z.object({
  status: z.literal(401),
  error: z.literal('unauthenticated'),
  message: z.string().min(1).describe('A sentence for a human. Never a reason a prober can use.'),
});

export type UnauthenticatedError = z.infer<typeof unauthenticatedErrorSchema>;

/** The body every `401` carries. One constant so no route can invent a second wording. */
export const UNAUTHENTICATED_BODY: UnauthenticatedError = {
  status: 401,
  error: 'unauthenticated',
  message: 'Authentification requise.',
};

/* ================================================================================================
 * The session, as the API sees it
 * ============================================================================================== */

/**
 * What the session middleware puts into the request context.
 *
 * Three fields, and every one of them is a **server** fact: the client cannot influence any of
 * them. `orgId` in particular is read from the user's row, never from a header or a body — a
 * tenant a caller can name is a tenant a caller can change.
 */
export const sessionContextSchema = z.object({
  userId: z.string().min(1).describe('better-auth user id.'),
  orgId: z
    .string()
    .min(1)
    .describe(
      'The organisation this user belongs to. Read from the stored user, never from the request. ' +
        'Every data-access call is scoped by it.',
    ),
  role: userRoleSchema,
});

export type SessionContext = z.infer<typeof sessionContextSchema>;

/* ================================================================================================
 * Invites — the only way an account comes into existence (D5)
 * ============================================================================================== */

/**
 * How long an invite is good for. Seven days.
 *
 * Long enough to survive a holiday, short enough that a link forwarded into a mailing list archive
 * stops working. It is a constant rather than a config because an invite lifetime that varies per
 * environment is one nobody can reason about during an incident.
 */
export const INVITE_TTL_DAYS = 7;

export const createInviteRequestSchema = z
  .object({
    email: z.email().describe('Who to invite. The invite is bound to this address.'),
    role: userRoleSchema.default('member').describe('Role the invitee receives on acceptance.'),
  })
  .describe(
    'Admin-only. There is no self-service equivalent: D5 forbids an open sign-up path, and ' +
      'ARCHITECTURE §8.1 sharpens it to "no open sign-up path exists in the API at all".',
  );

export type CreateInviteRequest = z.infer<typeof createInviteRequestSchema>;

/**
 * What the admin gets back.
 *
 * The token is returned **once, here, and is never readable again** — only its hash is stored. If
 * the admin loses it they issue a new invite, which is the same posture as any password reset link
 * and for the same reason: a token an operator can look up later is a token an operator can be
 * compelled or tricked into looking up.
 */
export const createInviteResponseSchema = z.object({
  id: z.string().min(1),
  email: z.email(),
  role: userRoleSchema,
  expiresAt: z.iso.datetime({ offset: true }),
  token: z
    .string()
    .min(1)
    .describe('Shown once. Only its hash is stored; it cannot be retrieved afterwards.'),
});

export type CreateInviteResponse = z.infer<typeof createInviteResponseSchema>;

export const acceptInviteRequestSchema = z.object({
  token: z.string().min(1),
  name: z.string().min(1).describe("The invitee's display name."),
  password: z
    .string()
    .min(12)
    .describe(
      'Minimum twelve characters. A length floor, not a character-class rule: composition rules ' +
        'push people towards Password1! and away from a passphrase.',
    ),
});

export type AcceptInviteRequest = z.infer<typeof acceptInviteRequestSchema>;

/**
 * What acceptance answers with.
 *
 * It carries the **email the account was created with**, and that is not a convenience: the account
 * takes its address from the invite row, never from the request body, so the client does not
 * otherwise know it. Without it the browser cannot sign the new user in — it would have to ask them
 * to retype an address it already holds, one field below the password they just chose.
 *
 * Disclosing it costs nothing: the caller proved possession of the single-use token, and the token
 * was sent to that address.
 *
 * Added in P2 wave 4, when the invite view was built and the omission became visible. The wave-2
 * route returned `{ ok, orgId }`; this is additive, and no existing caller reads the body at all.
 */
export const acceptInviteResponseSchema = z.object({
  ok: z.literal(true),
  orgId: z.string().min(1),
  email: z.email().describe('The address the account was created with, taken from the invite.'),
});

export type AcceptInviteResponse = z.infer<typeof acceptInviteResponseSchema>;

/** The signed-in user, for the UI's header. Never includes anything the client did not earn. */
export const meResponseSchema = z.object({
  userId: z.string().min(1),
  email: z.email(),
  name: z.string(),
  role: userRoleSchema,
  orgId: z.string().min(1),
  orgName: z.string().min(1),
});

export type MeResponse = z.infer<typeof meResponseSchema>;
